import { cloneDeep } from 'lodash';
import {
  fundingPolicyTypeDefs,
  FundingPolicyType,
  calcClientDerivatives,
  carePhaseDefs,
  CareEnvironment,
  CarePhase,
  checkPolicyCalculationTypeEquals,
} from '@shared';

import {
  formatBenefit,
  formatInflatedMonthlyBenefit,
  calculateMonthlyBenefit,
  calculateTotalBenefits,
  buildEliminationPeriodLabel,
  buildGrowthLabel,
} from '@/features/policies-comparison';
import {
  isShopInsuranceMoneyGuard,
  restoreIndependentLivingOnIndemnityFlip,
  scaleMoneyGuardBenefitFields,
} from '@/util/policyFundingSourceUtils';
import { readSourceQuoteField } from '@/components/policy-insight/policyInsightUtils';

export interface PremiumOption {
  premiumMonthlyCost?: number;
  lumpSumPayment?: number;
  limitedPayYears?: number;
  irr?: number;
  isIrrSuspect?: boolean;
  quoteOutdatedReason?: string;
  /**
   * Original billing frequency of the policy's recurring premium when known
   * (sourced from the quote that produced this funding source). Drives the
   * comparison view's unit label so an annually-billed policy is shown as
   * `$X/yr` rather than `$(X/12)/mo`. Undefined for manually-entered policies
   * where the original frequency wasn't captured — those default to `/mo`.
   */
  premiumFrequency?: 'Monthly' | 'Annually';
}

export interface NormalizedPolicy {
  fundingSourceId: string;
  fundingPolicyType?: FundingPolicyType;
  carrierId?: number;
  carrier?: string;
  productName?: string;
  initialMonthlyBenefit?: string;
  inflatedMonthlyBenefit?: string;
  initialTotalBenefit?: string;
  inflatedTotalBenefit?: string;
  inflationDescription?: string;
  benefitPeriodMonths?: number;
  eliminationPeriodLabel?: string;
  productDescription?: string;
  paymentMethod?: 'Indemnity' | 'Reimbursement';
  premiumMonthlyCost?: number;
  lumpSumPayment?: number;
  limitedPayYears?: number;
  premiumGuaranteed?: string;
  irr?: number;
  isIrrSuspect?: boolean;
  quoteOutdatedReason?: string;
  premiumOptions?: PremiumOption[];
  premiumFrequency?: 'Monthly' | 'Annually';
  isShopInsuranceMoneyGuard?: boolean;
  inflatedMonthlyBenefitNote?: string;
}

const NOT_GUARANTEED_CALCULATION_TYPES = [FundingPolicyType.longTermCareInsurance];

const PREMIUM_GUARANTEED_LABEL = 'Premiums are guaranteed and cannot be increased';
const PREMIUM_NOT_GUARANTEED_LABEL =
  'Premiums are not guaranteed and may be increased in the future';

function getPremiumGuaranteeLabel(fundingPolicyType: FundingPolicyType): string {
  if (fundingPolicyType === FundingPolicyType.careScoutCareAssurance) {
    return PREMIUM_NOT_GUARANTEED_LABEL;
  }

  if (checkPolicyCalculationTypeEquals(fundingPolicyType, NOT_GUARANTEED_CALCULATION_TYPES)) {
    return PREMIUM_NOT_GUARANTEED_LABEL;
  }

  return PREMIUM_GUARANTEED_LABEL;
}

export type PaymentMethodOverrides = Record<string, boolean>;

function hasAppliedOverride(
  policyFundingSources: MultipleFundingSources['ltcPolicy'],
  overrides: PaymentMethodOverrides,
): boolean {
  return Object.entries(overrides).some(([fundingSourceId, isIndemnity]) => {
    const policy = policyFundingSources[fundingSourceId];
    return policy && policy.isIndemnityPolicyPayment !== isIndemnity;
  });
}

function applyPaymentMethodOverrides(client: Client, overrides: PaymentMethodOverrides): Client {
  const nextClient = cloneDeep(client);
  const nextLtcPolicies = nextClient.multipleFundingSources.ltcPolicy;
  for (const [fundingSourceId, isIndemnity] of Object.entries(overrides)) {
    const policy = nextLtcPolicies[fundingSourceId];
    if (!policy || policy.isIndemnityPolicyPayment === isIndemnity) continue;

    // Shop-insurance MoneyGuard stores the contractual (reimbursement) daily max.
    // Flipping to indemnity reduces the payout to 80%; flipping back restores it.
    if (isShopInsuranceMoneyGuard(policy)) {
      const restoredIl = restoreIndependentLivingOnIndemnityFlip(policy, isIndemnity);
      if (restoredIl !== undefined) {
        policy.dailyIndependentLivingBenefitAmount = restoredIl;
      }
      Object.assign(policy, scaleMoneyGuardBenefitFields(policy, isIndemnity));
    }
    policy.isIndemnityPolicyPayment = isIndemnity;
  }
  return calcClientDerivatives(nextClient, null) as Client;
}

export function normalizePoliciesForComparison(
  policyFundingSources: MultipleFundingSources['ltcPolicy'],
  client: Client,
  paymentMethodOverrides?: PaymentMethodOverrides,
): NormalizedPolicy[] {
  const daysPerMonthForCalc = 30;

  const shouldApplyOverrides =
    paymentMethodOverrides && hasAppliedOverride(policyFundingSources, paymentMethodOverrides);
  const effectiveClient = shouldApplyOverrides
    ? applyPaymentMethodOverrides(client, paymentMethodOverrides!)
    : client;
  const effectivePolicies = shouldApplyOverrides
    ? effectiveClient.multipleFundingSources.ltcPolicy
    : policyFundingSources;

  return Object.entries(effectivePolicies).map(([fundingSourceId, policyFundingSource]) => {
    const fundingPolicyType = policyFundingSource.policyType as FundingPolicyType;

    const policyTypeDef = fundingPolicyTypeDefs[fundingPolicyType];
    const productName = policyTypeDef?.label;
    const productDescription = policyTypeDef?.description;
    const carrierId = policyTypeDef?.carrierId;
    const carrier = policyTypeDef?.carrier;
    const premiumMonthlyCost = policyFundingSource.policyPremiumMonthlyCost ?? undefined;
    const lumpSumPayment = policyFundingSource.policyLumpSumPayment ?? undefined;
    const limitedPayYears = policyFundingSource.policyLimitedPayYears ?? undefined;
    const irr = policyFundingSource.policyProjectedInternalRateOfReturn ?? undefined;
    const isIrrSuspect = policyFundingSource.isIrrSuspect ?? undefined;
    const quoteOutdatedReason = policyFundingSource.quoteOutdatedReason ?? undefined;
    // Recover the policy's original billing frequency from the quote it was
    // built from. `policyPremiumMonthlyCost` is always stored as a per-month
    // value (annual quotes are divided by 12 in build-policy-funding-source),
    // so without this we'd display annually-billed policies as `$(X/12)/mo`,
    // which doesn't match the carrier's actual bill.
    const rawSourceQuote = policyFundingSource.updatedQuote ?? policyFundingSource.sourceQuote;
    const rawPremiumFrequency = readSourceQuoteField(rawSourceQuote, 'premiumFrequency');
    const premiumFrequency: 'Monthly' | 'Annually' | undefined =
      rawPremiumFrequency === 'Monthly' || rawPremiumFrequency === 'Annually'
        ? rawPremiumFrequency
        : undefined;

    const benefitPeriodMonths = policyFundingSource.policyBenefitPeriodMonths ?? undefined;
    const eliminationPeriodLabel = buildEliminationPeriodLabel(
      policyFundingSource.homeCareWaitingPeriodDays ?? undefined,
      policyFundingSource.facilityCareWaitingPeriodDays ?? undefined,
      fundingPolicyType,
    );

    const phaseCalcsForPolicy = effectiveClient.policyPhaseCalculations?.[fundingSourceId];

    const currentCarePhase = effectiveClient.intakeSurvey?.clientCurrentCarePhase;
    const firstCarePhaseIndex = currentCarePhase ? carePhaseDefs[currentCarePhase].index : 0;

    const {
      initialMonthlyBenefit,
      inflatedMonthlyBenefit,
      inflatedMonthlyBenefitBase,
      inflatedMonthlyBenefitRider,
    } = calculateMonthlyBenefit(
      phaseCalcsForPolicy,
      daysPerMonthForCalc,
      fundingPolicyType,
      firstCarePhaseIndex,
    );

    const paymentMethod: 'Indemnity' | 'Reimbursement' | undefined =
      policyFundingSource.isIndemnityPolicyPayment ? 'Indemnity' : 'Reimbursement';

    const { initialTotalBenefit, inflatedTotalBenefit } = calculateTotalBenefits(
      policyFundingSource,
      phaseCalcsForPolicy,
      firstCarePhaseIndex,
    );

    const growthLabel = buildGrowthLabel(policyFundingSource, phaseCalcsForPolicy);

    const premiumGuaranteed = getPremiumGuaranteeLabel(fundingPolicyType);

    // Legacy-priced policies only. `isIndependentLivingBenefitZeroedOut` still
    // fires in the legacy phase calc for shop-added reimbursement policies on an
    // Independent Living phase, producing no per-phase daily benefit. Fall back
    // to the home-care daily x 30 for Day One — the same formula the phase calc
    // would use if the environment were home care — and flag the inflated cell,
    // since that figure is phase-end-year dependent and we do not want to print
    // a fuzzy number.
    //
    // Engine-priced policies cover Independent Living as home care, so there is
    // no zero-out to paper over and the fallback must not fire for them. Keyed
    // off `policyProjectionSource` rather than a client-level setting, so it
    // tracks the engine that actually priced THIS policy.
    const firstCarePhase: CarePhase = currentCarePhase ?? CarePhase.earlyCare;
    const firstAppliedEnv = effectiveClient.appliedCareEnvironments?.[firstCarePhase];
    const shouldUseIlFallback =
      policyFundingSource.policyProjectionSource !== 'engine' &&
      !policyFundingSource.isIndemnityPolicyPayment &&
      firstAppliedEnv === CareEnvironment.independentLiving &&
      !initialMonthlyBenefit;
    const fallbackDailyHome = policyFundingSource.dailyHomeBenefitAmount;
    const effectiveInitialMonthlyBenefit =
      shouldUseIlFallback && typeof fallbackDailyHome === 'number' && fallbackDailyHome > 0
        ? fallbackDailyHome * daysPerMonthForCalc
        : initialMonthlyBenefit;
    const inflatedMonthlyBenefitNote = shouldUseIlFallback
      ? "Independent Living is set to $0 for reimbursement policies. Change the care environment for the client's first care phase to see the inflated benefit amount."
      : undefined;

    return {
      fundingSourceId,
      fundingPolicyType,
      carrierId,
      carrier,
      productDescription,
      productName,
      inflationDescription: growthLabel,
      benefitPeriodMonths,
      eliminationPeriodLabel,
      paymentMethod,
      lumpSumPayment,
      premiumMonthlyCost,
      limitedPayYears,
      premiumFrequency,
      premiumGuaranteed,
      irr,
      isIrrSuspect,
      quoteOutdatedReason,
      initialMonthlyBenefit: effectiveInitialMonthlyBenefit
        ? formatBenefit(effectiveInitialMonthlyBenefit)
        : '-',
      inflatedMonthlyBenefit:
        inflatedMonthlyBenefit || inflatedMonthlyBenefitBase || inflatedMonthlyBenefitRider
          ? formatInflatedMonthlyBenefit(
              inflatedMonthlyBenefit,
              inflatedMonthlyBenefitBase,
              inflatedMonthlyBenefitRider,
            )
          : '-',
      initialTotalBenefit: formatBenefit(initialTotalBenefit),
      inflatedTotalBenefit: formatBenefit(inflatedTotalBenefit),
      isShopInsuranceMoneyGuard: isShopInsuranceMoneyGuard(policyFundingSource),
      inflatedMonthlyBenefitNote,
    };
  });
}

/**
 * Rounds a formatted currency string to the nearest $10 so that small
 * differences ($1-2) between payment options don't prevent
 * otherwise-identical products from merging.
 */
function bucketBenefit(formatted: string | undefined): string {
  if (!formatted || formatted === '-' || formatted === 'Unlimited') return formatted ?? '';
  const num = parseFloat(formatted.replace(/[$,]/g, ''));
  if (isNaN(num)) return formatted;
  return String(Math.round(num / 10) * 10);
}

function buildMergeKey(policy: NormalizedPolicy): string {
  if (policy.fundingPolicyType == null) return policy.fundingSourceId;

  return [
    policy.fundingPolicyType,
    bucketBenefit(policy.initialMonthlyBenefit),
    bucketBenefit(policy.inflatedMonthlyBenefit),
    bucketBenefit(policy.initialTotalBenefit),
    bucketBenefit(policy.inflatedTotalBenefit),
    policy.benefitPeriodMonths,
  ].join('|');
}

export function mergeSameCarrierPolicies(policies: NormalizedPolicy[]): NormalizedPolicy[] {
  const groupMap = new Map<string, NormalizedPolicy[]>();
  const groupOrder: string[] = [];

  for (const policy of policies) {
    const key = buildMergeKey(policy);
    const group = groupMap.get(key);
    if (group) {
      group.push(policy);
    } else {
      groupMap.set(key, [policy]);
      groupOrder.push(key);
    }
  }

  return groupOrder.map(key => {
    const group = groupMap.get(key)!;
    if (group.length === 1) return group[0];

    const primary = group[0];
    const seen = new Set<string>();
    const premiumOptions: PremiumOption[] = [];

    for (const p of group) {
      const dedupKey = [
        p.premiumMonthlyCost,
        p.lumpSumPayment,
        p.limitedPayYears,
        p.premiumFrequency,
      ].join('|');
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      premiumOptions.push({
        premiumMonthlyCost: p.premiumMonthlyCost,
        lumpSumPayment: p.lumpSumPayment,
        limitedPayYears: p.limitedPayYears,
        irr: p.irr,
        isIrrSuspect: p.isIrrSuspect,
        quoteOutdatedReason: p.quoteOutdatedReason,
        premiumFrequency: p.premiumFrequency,
      });
    }

    return { ...primary, premiumOptions };
  });
}
